/* eslint-disable no-restricted-imports */
import React, {useState} from 'react'
import clsx from 'clsx'
import {
    DevCardStats,
    DiceStats,
    Overview,
    ResourceCardStats,
    ActivityStats,
    ResourceStats,
} from './tabs'
import {PlayerTable} from './PlayerTable'
import {useUIGameEndContext} from '../../../contexts/UIGameEndContext'
import {T} from '../../../common/T'
import {TranslationObject} from '@colonist/i18n'
import {FADE_IN_ANIMATION_DELAYS} from '../constants'
import {Sound} from '../../../utils/sound'
import {useUIGameManagerContext} from '../../../contexts/UIGameManagerContext'
import styles from './Content.module.scss'
import {FadeInContainer} from '../../../common/FadeInContainer'
import {CA} from '../../../../../../code/client/shared/scripts/analytics/Analytics'

const tabContentMapping = {
    overview: <Overview />,
    'dice-stats': <DiceStats />,
    'res-card-stats': <ResourceCardStats />,
    'dev-card-stats': <DevCardStats />,
    'activity-stats': <ActivityStats />,
    'resource-stats': <ResourceStats />,
}

interface Tab {id: string, label: TranslationObject, analyticEvent: CA.Label}

export const Tabs = ({
    tabs,
    selected,
    onTabClick,
}: {
    tabs: Tab[]
    selected: string
    onTabClick: (tab: Tab) => void
}) => <div className={styles.tabs}>
    {tabs.map((tab) => (
        <div
            className={clsx(styles.tab, {[styles.active]: selected === tab.id})}
            key={tab.id}
            onClick={() => {
                onTabClick(tab)
                Sound.playSound(Sound.SoundType.Click)
            }}
        >
            <T id={tab.label} />
        </div>
    ))}
</div>

export const Content = ({disableAnimation}: {disableAnimation: boolean}) => {
    const {isPlayerHasRankedState} = useUIGameEndContext()
    const gameManager = useUIGameManagerContext()
    const isReplay = gameManager.gameController.isReplay

    const allTabs: Tab[] = [
        {id: 'overview', label: {key: 'strings:game.endGame.overview.title'}, analyticEvent: CA.Label.ClickedEndGameOverview},
        {id: 'dice-stats', label: {key: 'strings:game.endGame.dice.title'}, analyticEvent: CA.Label.ClickedEndGameDiceStats},
        {id: 'res-card-stats', label: {key: 'strings:game.endGame.resourceCards.title'}, analyticEvent: CA.Label.ClickedEndGameResourceCardStats},
        {
            id: 'dev-card-stats',
            label: {
                key: 'strings:game.endGame.devCards.title',
                options: {context: gameManager.gameState.gameModeData.translationKeyContext},
            },
            analyticEvent: CA.Label.ClickedEndGameDevelopmentCardStats,
        },
        {id: 'activity-stats', label: {key: 'strings:game.endGame.activity.title'}, analyticEvent: CA.Label.ClickedEndGameActivityStats},
        {id: 'resource-stats', label: {key: 'strings:game.endGame.resource.title'}, analyticEvent: CA.Label.ClickedEndGameResourceStats},
    ]

    const [selectedTab, setSelectedTab] = useState(allTabs[0].id)

    const handleTabClick = (tab: Tab) => {
        setSelectedTab(tab.id)
        CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, tab.analyticEvent)
    }

    return (
        <FadeInContainer
            animationDelayMs={isPlayerHasRankedState && !isReplay ? FADE_IN_ANIMATION_DELAYS.contentRankedGame : FADE_IN_ANIMATION_DELAYS.content}
            animationDurationMs={FADE_IN_ANIMATION_DELAYS.transitionDuration}
            className={clsx(styles.contentContainer)}
            disableAnimation={disableAnimation}>
            <div className={styles.wrapper}>
                <Tabs
                    onTabClick={(tab) => handleTabClick(tab)}
                    selected={selectedTab}
                    tabs={allTabs}
                />
                <div className={styles.tabContent}>
                    <PlayerTable />
                    {tabContentMapping[selectedTab]}
                </div>
            </div>
        </FadeInContainer>
    )
}
